// Lib
import React, { ReactNode, forwardRef, useEffect, useRef } from 'react';

// Utils
import { cleanSvgForInnerHtml } from '../../utils/svgUtils';
import { getDrawingSvg, isTransparencyEnabled } from '../../../common/elements/utils/elementPropertyUtils';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';

// Styles
import './DrawingSvg.scss';

const VIEW_BOX_REGEX = /viewBox=".+?"/;

interface DrawingSvgProps {
    children?: ReactNode;
    element: ImMNElement;
    viewBox?: string;
    onDoubleClick?: () => void;
    isPresentational?: boolean;
    isDragPreview?: boolean;
    isSelected?: boolean;
    isRemotelySelected?: boolean;
    enableDrag?: () => void;
    disableDrag?: () => void;
}

const getSvgString = (element: ImMNElement, viewBox?: string) => {
    const svg = getDrawingSvg(element);

    if (!viewBox || !svg) return svg;

    if (!svg.includes(viewBox)) {
        return svg.replace('<svg ', `<svg viewBox="${viewBox}"`);
    }

    return svg.replace(VIEW_BOX_REGEX, `viewBox="${viewBox}"`);
};

export const SimpleDrawingSvg = forwardRef<HTMLDivElement, DrawingSvgProps>(function SimpleDrawingSvg(props, ref) {
    const { children, element, onDoubleClick, viewBox } = props;

    const svg = getSvgString(element, viewBox);

    return (
        <div className="DrawingSvg" ref={ref} onDoubleClick={onDoubleClick}>
            <div className="svg-content" dangerouslySetInnerHTML={cleanSvgForInnerHtml(svg)} />
            {children}
        </div>
    );
});

const DrawingSvgPathDraggable: React.FC<DrawingSvgProps> = (props) => {
    const { disableDrag, enableDrag } = props;

    const drawingNodeRef = useRef<HTMLDivElement>(null);

    // On mount, prevent drag, much like the Line.js so that hover events work correctly
    useEffect(() => {
        disableDrag && disableDrag();

        return () => {
            enableDrag && enableDrag();
        };
    }, []);

    return <SimpleDrawingSvg ref={drawingNodeRef} {...props} />;
};

const DrawingSvgSwitch: React.FC<DrawingSvgProps> = (props) => {
    const { isPresentational, isDragPreview, isSelected, isRemotelySelected, enableDrag, element } = props;

    if (
        !isTransparencyEnabled(element) ||
        isSelected ||
        isRemotelySelected ||
        isPresentational ||
        isDragPreview ||
        !enableDrag
    ) {
        return <SimpleDrawingSvg {...props} />;
    }

    return <DrawingSvgPathDraggable {...props} />;
};

export default DrawingSvgSwitch;
